import { useEffect, useState } from "react";
import { ClientEvent, RoomEvent, type MatrixClient, type Room } from "matrix-js-sdk";
import { listConversations, type ConversationSummary } from "../../matrix/rooms";

/**
 * Live conversation list. Recomputes summaries on any event that could change
 * ordering/preview/unread state. Also auto-joins rooms we're invited to so
 * both sides of a new DM see the conversation.
 */
export function useConversations(client: MatrixClient): ConversationSummary[] {
  const [conversations, setConversations] = useState<ConversationSummary[]>(() =>
    listConversations(client),
  );

  useEffect(() => {
    let frame = 0;
    const refresh = () => {
      // Coalesce bursts of events into a single recompute.
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setConversations(listConversations(client)));
    };

    const onMyMembership = (room: Room, membership: string) => {
      if (membership === "invite") {
        client.joinRoom(room.roomId).catch(() => undefined);
      }
      refresh();
    };

    client.on(RoomEvent.Timeline, refresh);
    client.on(RoomEvent.Receipt, refresh);
    client.on(RoomEvent.MyMembership, onMyMembership);
    client.on(ClientEvent.Room, refresh);
    client.on(RoomEvent.Name, refresh);

    refresh();

    return () => {
      cancelAnimationFrame(frame);
      client.removeListener(RoomEvent.Timeline, refresh);
      client.removeListener(RoomEvent.Receipt, refresh);
      client.removeListener(RoomEvent.MyMembership, onMyMembership);
      client.removeListener(ClientEvent.Room, refresh);
      client.removeListener(RoomEvent.Name, refresh);
    };
  }, [client]);

  return conversations;
}
